import React from 'react';
import { Typography } from 'antd';
import AdminProductCreateCard from '../AdminProductCreateCard';
import AdminProductsCard from '../AdminProductsCard';
import { ADMIN_PRODUCTS_PAGE_SIZE } from '../adminProductForm.utils';
import { useAdminProductFormController } from '../useAdminProductFormController';
import './AdminProductForm.scss';

const { Title, Text } = Typography;

type AdminProductFormProps = {
  createOnly?: boolean;
};

const AdminProductForm: React.FC<AdminProductFormProps> = ({ createOnly = false }) => {
  const controller = useAdminProductFormController();
  const isCreateOpen = createOnly || controller.isCreateOpen;

  if (!controller.user?.isAdmin) {
    return (
      <div className="admin-product-form">
        <Title level={3}>Access denied</Title>
        <Text>This page is available to administrators only.</Text>
      </div>
    );
  }

  return (
    <div className="admin-product-form">
      <div className="admin-product-form__header">
        <Title level={3}>{createOnly ? 'Create Product' : 'Manage Products'}</Title>
        <Text type="secondary">
          {createOnly
            ? 'Create a catalog product with material-specific sizes, images, and variant stock.'
            : 'Update stock for existing products, adjust material data, and delete products with related data.'}
        </Text>
      </div>
      {createOnly && (
        <AdminProductCreateCard
          form={controller.form}
          types={controller.types}
          materialTemplates={controller.materialTemplates}
          filteredSizes={controller.filteredSizes}
          selectedTypeRequiresSizes={controller.selectedTypeRequiresSizes}
          loadingOptions={controller.loadingOptions}
          submitting={controller.submitting}
          isOpen={isCreateOpen}
          createCardRef={controller.createCardRef}
          onSubmit={controller.handleCreateProduct}
          onToggleOpen={controller.setIsCreateOpen}
          alwaysOpen={createOnly}
        />
      )}
      {!createOnly && (
        <AdminProductsCard
          products={controller.products}
          loadingProducts={controller.loadingProducts}
          loadingMoreProducts={controller.loadingMoreProducts}
          currentPage={controller.currentProductsPage}
          deletingProductId={controller.deletingProductId}
          savingStockId={controller.savingStockId}
          savingPriceId={controller.savingPriceId}
          savingSizesId={controller.savingSizesId}
          loadingMaterialDetailsById={controller.loadingMaterialDetailsById}
          draftStockById={controller.draftStockById}
          draftPriceById={controller.draftPriceById}
          sizes={controller.sizes}
          productsPageSize={ADMIN_PRODUCTS_PAGE_SIZE}
          productsTotal={controller.productsTotal}
          searchTerm={controller.searchTerm}
          activeSearchTerm={controller.activeSearchTerm}
          searchingProducts={controller.searchingProducts}
          onSearchTermChange={controller.setSearchTerm}
          onSearch={controller.handleSearchProducts}
          onClearSearch={controller.handleClearSearch}
          onPageChange={controller.handleProductPageChange}
          onLoadMaterialDetails={controller.handleLoadMaterialDetails}
          onDeleteProduct={controller.handleDeleteProduct}
          onStockStep={controller.handleStockStep}
          onDraftStockInput={controller.handleDraftStockInput}
          onDraftPriceInput={controller.handleDraftPriceInput}
          onCommitDraftStock={controller.commitDraftStock}
          onCommitDraftPrice={controller.commitDraftPrice}
          onCommitMaterialSizes={controller.handleMaterialSizesChange}
          onAvailabilityChange={controller.handleAvailabilityChange}
          onVariantAvailabilityChange={controller.handleVariantAvailabilityChange}
          onUploadMaterialImages={controller.handleUploadMaterialImages}
          onDeleteMaterialImage={controller.handleDeleteMaterialImage}
        />
      )}
    </div>
  );
};

export default AdminProductForm;
